import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft, CircleCheck, CirclePause, CircleSlash, ClipboardCheck, Clock, Hammer, Info, Pause, Play, TriangleAlert, Zap,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import {
  CampaignPlanRow, PlanFinding, PlanStatus, PlanValidation, getMicrosoftCampaignTemplates,
  microsoftCampaignPlanAction, listMicrosoftCampaignPlans, saveMicrosoftCampaignPlan, validateMicrosoftCampaignPlan,
} from '../services/microsoftAdsApi';

const PINE = '#0F3F34';
const ORANGE = '#F07824';
const MINT = '#F4F8F6';
const STATUS = { good: '#0ca30c', warning: '#fab219', critical: '#d03b3b', neutral: '#8a8984' };
const TZ = 'Europe/Copenhagen';

const PLAN_STATUS: Record<PlanStatus, { label: string; color: string; Icon: LucideIcon }> = {
  draft: { label: 'Draft, has problems', color: STATUS.critical, Icon: TriangleAlert },
  validated: { label: 'Ready to build', color: STATUS.good, Icon: CircleCheck },
  queued: { label: 'Queued', color: STATUS.warning, Icon: Clock },
  building: { label: 'Building', color: STATUS.warning, Icon: Clock },
  dry_run: { label: 'Dry run done', color: STATUS.neutral, Icon: Info },
  built: { label: 'Built, paused in Microsoft Ads', color: STATUS.good, Icon: CirclePause },
  failed: { label: 'Failed', color: STATUS.critical, Icon: TriangleAlert },
  active: { label: 'Active', color: STATUS.good, Icon: CircleCheck },
  paused: { label: 'Paused', color: STATUS.neutral, Icon: CirclePause },
};

function when(value: string | null | undefined) {
  if (!value) return '–';
  return new Date(value).toLocaleString('da-DK', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short' });
}

function StatusPill({ status }: { status: PlanStatus }) {
  const s = PLAN_STATUS[status] ?? { label: status, color: STATUS.neutral, Icon: CircleSlash };
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
      <s.Icon size={12} color={s.color} aria-hidden="true" />
      {s.label}
    </span>
  );
}

type Group = { level: 'error' | 'warning'; message: string; fields: string[] };

function groupFindings(findings: PlanFinding[]): Group[] {
  const groups = new Map<string, Group>();
  for (const f of findings) {
    const key = `${f.level}|${f.message}`;
    const g = groups.get(key) ?? { level: f.level, message: f.message, fields: [] };
    if (f.field) g.fields.push(f.field);
    groups.set(key, g);
  }
  return Array.from(groups.values());
}

function Finding({ level, message, fields }: Group) {
  const shown = fields.slice(0, 3);
  return (
    <li className="flex items-start gap-2 text-sm text-gray-700">
      {level === 'error'
        ? <TriangleAlert size={15} color={STATUS.critical} className="mt-0.5 shrink-0" aria-label="Error" />
        : <Info size={15} color={STATUS.warning} className="mt-0.5 shrink-0" aria-label="Warning" />}
      <span className="min-w-0 break-words">
        {message}
        {shown.length > 0 && (
          <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
            {shown.map((f) => <code key={f} className="break-all rounded bg-gray-100 px-1 py-0.5">{f}</code>)}
            {fields.length > shown.length && <span>and {fields.length - shown.length} more</span>}
          </span>
        )}
      </span>
    </li>
  );
}

function Results({ validation }: { validation: PlanValidation }) {
  const ads = validation.preview.length;
  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-800">
        {validation.ok
          ? <CircleCheck size={16} color={STATUS.good} aria-hidden="true" />
          : <TriangleAlert size={16} color={STATUS.critical} aria-hidden="true" />}
        {validation.ok
          ? `Ready: ${ads} Bing RSA ads, ${validation.daily_budget ?? 0} ${validation.currency ?? ''} per day`
          : `${validation.errors.length} problems to fix before this can be built`}
      </p>
      {validation.errors.length > 0 && (
        <ul className="space-y-2">{groupFindings(validation.errors).map((g) => <Finding key={`e${g.message}`} {...g} />)}</ul>
      )}
      {validation.warnings.length > 0 && (
        <ul className="space-y-2">{groupFindings(validation.warnings).map((g) => <Finding key={`w${g.message}`} {...g} />)}</ul>
      )}
      {ads > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Microsoft Bing SERP & RSA asset preview
          </h3>
          <div className="max-h-[34rem] space-y-3 overflow-y-auto pr-1">
            {validation.preview.map((ad) => {
              const displayPath = [ad.path1, ad.path2].filter(Boolean).join('/');
              const serpTitle = ad.headlines.slice(0, 3).join(' | ');
              const serpDesc = ad.descriptions.slice(0, 2).join(' ');
              return (
                <div key={`${ad.ad_group}:${ad.ad}`} className="rounded-lg border border-gray-200 bg-white p-3.5">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-400">
                    <span>{ad.ad_group} › {ad.ad}</span>
                    <span>{ad.headlines.length} headlines · {ad.descriptions.length} descriptions · {ad.keywords_count} keywords</span>
                  </div>

                  {/* Bing Search Ad Preview Card */}
                  <div className="mt-2 rounded-md border border-gray-100 bg-gray-50/70 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className="rounded border border-gray-300 px-1 py-0.2 text-[10px] font-semibold text-gray-700">Ad</span>
                      <span className="truncate">{ad.final_url || 'https://simplyloan.se'}{displayPath ? ` › ${displayPath}` : ''}</span>
                    </div>
                    <p className="mt-1 text-base font-medium text-blue-700 hover:underline">
                      {serpTitle}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-700">
                      {serpDesc}
                    </p>
                  </div>

                  {/* All Headlines & Descriptions with char counts */}
                  <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-gray-400">Headlines (max 30 chars)</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {ad.headlines.map((h, idx) => (
                          <span
                            key={`${idx}-${h}`}
                            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${
                              h.length > 30 ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-700'
                            }`}
                          >
                            {h}
                            <span className="text-[9px] text-gray-400">{h.length}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-gray-400">Descriptions (max 90 chars)</p>
                      <div className="mt-1 space-y-1">
                        {ad.descriptions.map((d, idx) => (
                          <div
                            key={`${idx}-${d}`}
                            className={`rounded border px-2 py-1 text-[11px] ${
                              d.length > 90 ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-700'
                            }`}
                          >
                            {d} <span className="text-[9px] text-gray-400">({d.length}/90)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {ad.sample_keywords.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                      <span className="font-medium text-gray-400">Keywords:</span>
                      {ad.sample_keywords.map((kw) => (
                        <code key={kw} className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">{kw}</code>
                      ))}
                      {ad.keywords_count > ad.sample_keywords.length && (
                        <span>+{ad.keywords_count - ad.sample_keywords.length} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SimplyloanMicrosoftCampaigns() {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState('');
  const [templateFile, setTemplateFile] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [validation, setValidation] = useState<PlanValidation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const plansQuery = useQuery({
    queryKey: ['microsoft-campaign-plans'],
    queryFn: listMicrosoftCampaignPlans,
    refetchInterval: (q) =>
      (q.state.data?.plans ?? []).some((p) => p.status === 'queued' || p.status === 'building') ? 10_000 : false,
  });
  const templatesQuery = useQuery({
    queryKey: ['microsoft-campaign-templates'],
    queryFn: getMicrosoftCampaignTemplates,
    staleTime: Infinity,
  });

  const parsed = () => {
    try {
      const value = JSON.parse(editor);
      setParseError(null);
      return value;
    } catch (e) {
      setParseError(`Not valid JSON: ${(e as Error).message}`);
      return undefined;
    }
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['microsoft-campaign-plans'] });

  const validate = useMutation({
    mutationFn: validateMicrosoftCampaignPlan,
    onSuccess: (v) => { setValidation(v); setNotice(null); },
    onError: (e: Error) => setNotice(e.message),
  });

  const save = useMutation({
    mutationFn: async ({ plan, buildNow }: { plan: unknown; buildNow: boolean }) => {
      const saved = await saveMicrosoftCampaignPlan(plan);
      if (saved.errors) {
        setValidation({
          ok: !!saved.ok,
          errors: saved.errors,
          warnings: saved.warnings ?? [],
          preview: saved.preview ?? [],
          daily_budget: null,
          currency: null,
        });
      }
      if (buildNow && saved.status === 'validated' && saved.plan_id) {
        const built = await microsoftCampaignPlanAction('build-now', saved.plan_id);
        return { ...saved, status: built.status, reason: built.reason, created_objects: built.created_objects };
      }
      return saved;
    },
    onSuccess: (r) => {
      setNotice(
        r.reason ??
          (r.status === 'built' || r.status === 'dry_run'
            ? `Saved and built (${r.status}${r.created_objects != null ? `, ${r.created_objects} objects` : ''}).`
            : r.status === 'validated'
              ? 'Saved and validated. Build it from the plan list when ready.'
              : `Saved as ${r.status}. Fix the problems listed and save again.`),
      );
      refresh();
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const act = useMutation({
    mutationFn: ({ action, plan }: { action: 'queue' | 'build-now' | 'activate' | 'pause'; plan: CampaignPlanRow }) =>
      microsoftCampaignPlanAction(action, plan.id),
    onSuccess: (r) => {
      setNotice(
        r.reason ??
          `Plan ${r.status}${r.created_objects != null ? ` (${r.created_objects} objects created)` : ''}.`,
      );
      refresh();
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const loadTemplate = (file: string) => {
    setTemplateFile(file);
    const t = templatesQuery.data?.templates.find((x) => x.file === file);
    if (t) {
      setEditor(JSON.stringify(t.spec, null, 2));
      setValidation(null);
      setParseError(null);
    }
  };

  const loadPlan = (p: CampaignPlanRow) => {
    setEditor(JSON.stringify(p.spec, null, 2));
    setTemplateFile('');
    setValidation(null);
    setNotice(
      p.objects > 0
        ? `Loaded ${p.plan_key}. It already has Microsoft Ads objects, so save changes under a new plan_key.`
        : `Loaded ${p.plan_key}.`,
    );
  };

  const confirmActivate = (p: CampaignPlanRow) => {
    const budget = p.validation?.daily_budget;
    const msg = `Switch "${p.name}" on in Microsoft Ads? It starts spending${budget ? ` up to ${budget} ${p.validation?.currency ?? ''} per day` : ''}.`;
    if (window.confirm(msg)) act.mutate({ action: 'activate', plan: p });
  };

  const data = plansQuery.data;
  const busy = validate.isPending || save.isPending || act.isPending;
  const plans = useMemo(() => data?.plans ?? [], [data]);

  return (
    <MainLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/simplyloan/microsoft" className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <ArrowLeft size={12} aria-hidden="true" /> Microsoft Ads performance & search queries
            </Link>
            <h1 className="text-2xl font-semibold" style={{ color: PINE }}>Microsoft Ads campaign builder</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Validates RSA character limits, Microsoft Financial Services policy, and SE/NO credit marketing laws. Builds campaigns Paused.
            </p>
          </div>
          {data && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-600">
                <Hammer size={12} color={data.mode === 'live' ? STATUS.warning : PINE} aria-hidden="true" />
                Builder: {data.mode === 'live' ? 'live (creates in Microsoft Ads)' : 'dry run (sends nothing)'}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-600">
                <Play size={12} color={data.activation_allowed ? STATUS.warning : STATUS.neutral} aria-hidden="true" />
                Activation from here: {data.activation_allowed ? 'on' : 'off'}
              </span>
            </div>
          )}
        </div>

        {notice && (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700" style={{ backgroundColor: MINT }}>
            <Info size={15} color={PINE} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{notice}</span>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold" style={{ color: PINE }}>Plans</h2>
            {plansQuery.isFetching && <span className="text-xs text-gray-400">Refreshing…</span>}
          </div>
          {plansQuery.isError && (
            <p className="px-4 py-6 text-sm text-gray-600">Could not load plans: {(plansQuery.error as Error).message}</p>
          )}
          {data && plans.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-gray-500">No Microsoft Ads plans yet. Start from a template below.</p>
          )}
          {plans.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead style={{ backgroundColor: MINT }}>
                  <tr className="text-left text-xs font-medium text-gray-600">
                    <th scope="col" className="px-4 py-2.5">Plan</th>
                    <th scope="col" className="px-3 py-2.5">Status</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Budget per day</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Microsoft objects</th>
                    <th scope="col" className="px-3 py-2.5">Updated</th>
                    <th scope="col" className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plans.map((p) => (
                    <tr key={p.id} className="align-top hover:bg-gray-50">
                      <td className="max-w-[24rem] px-4 py-2.5">
                        <p className="truncate font-medium text-gray-900" title={p.name}>{p.name}</p>
                        <p className="text-xs text-gray-400">
                          {p.plan_key} · {p.geo.toUpperCase()}
                          {p.campaign_id && !p.campaign_id.startsWith('{{') ? ` · campaign ${p.campaign_id}` : ''}
                        </p>
                        {p.last_error && (
                          <p className="mt-1 text-xs text-gray-600">
                            <TriangleAlert size={12} color={STATUS.critical} className="mr-1 inline" aria-hidden="true" />
                            {p.last_error}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2.5"><StatusPill status={p.status} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                        {p.validation?.daily_budget != null
                          ? `${p.validation.daily_budget.toLocaleString('da-DK')} ${p.validation.currency ?? ''}`
                          : '–'}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{p.objects}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                        {when(p.updated_at)}<br />{p.created_by ?? ''}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => loadPlan(p)}
                            className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                          >
                            Load
                          </button>
                          {['validated', 'dry_run', 'failed', 'queued'].includes(p.status) && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act.mutate({ action: 'build-now', plan: p })}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-60"
                              style={{ backgroundColor: PINE }}
                            >
                              <Zap size={12} aria-hidden="true" />
                              {data?.mode === 'live' ? 'Build paused now' : 'Run dry-run now'}
                            </button>
                          )}
                          {p.status === 'built' && (
                            <button
                              type="button"
                              disabled={busy || !data?.activation_allowed}
                              onClick={() => confirmActivate(p)}
                              title={
                                data?.activation_allowed
                                  ? 'Switch the campaign on in Microsoft Ads'
                                  : 'Activation from the dashboard is off; switch it on in Microsoft Ads or enable MICROSOFT_CAMPAIGN_ALLOW_ACTIVATION=true'
                              }
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                              style={{ backgroundColor: ORANGE }}
                            >
                              <Play size={12} aria-hidden="true" /> Activate
                            </button>
                          )}
                          {(p.status === 'active' || p.status === 'built') && data?.mode === 'live' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => act.mutate({ action: 'pause', plan: p })}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                              <Pause size={12} aria-hidden="true" /> Pause
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold" style={{ color: PINE }}>Plan JSON</h2>
              <select
                value={templateFile}
                onChange={(e) => loadTemplate(e.target.value)}
                aria-label="Start from a template"
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700"
              >
                <option value="">Start from a template…</option>
                {(templatesQuery.data?.templates ?? []).map((t) => (
                  <option key={t.file} value={t.file}>{String((t.spec as any).campaign?.name ?? t.file)}</option>
                ))}
              </select>
            </div>
            <textarea
              value={editor}
              onChange={(e) => setEditor(e.target.value)}
              spellCheck={false}
              aria-label="Plan JSON"
              placeholder='{"plan_key": "...", "geo": "se", "campaign": {...}, "ad_groups": [...]}'
              className="mt-3 h-[30rem] w-full rounded-lg border border-gray-200 p-3 font-mono text-xs leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            {parseError && (
              <p className="mt-2 text-xs text-gray-700">
                <TriangleAlert size={12} color={STATUS.critical} className="mr-1 inline" aria-hidden="true" />
                {parseError}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy || !editor.trim()}
                onClick={() => { const p = parsed(); if (p !== undefined) validate.mutate(p); }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <ClipboardCheck size={14} aria-hidden="true" /> Check plan
              </button>
              <button
                type="button"
                disabled={busy || !editor.trim()}
                onClick={() => { const p = parsed(); if (p !== undefined) save.mutate({ plan: p, buildNow: false }); }}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: PINE }}
              >
                Save plan
              </button>
              <button
                type="button"
                disabled={busy || !editor.trim()}
                onClick={() => { const p = parsed(); if (p !== undefined) save.mutate({ plan: p, buildNow: true }); }}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: ORANGE }}
              >
                <Hammer size={14} aria-hidden="true" /> Save and build now
              </button>
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-gray-200 p-4" style={{ backgroundColor: MINT }}>
            <h2 className="mb-3 text-sm font-semibold" style={{ color: PINE }}>Compliance & Bing SERP preview</h2>
            {validation
              ? <Results validation={validation} />
              : (
                <p className="text-sm text-gray-500">
                  Check a plan to verify RSA 30/90 character limits, Microsoft Financial Services policy, SE/NO credit laws, and preview every Bing RSA Search ad before anything reaches Microsoft Advertising.
                </p>
              )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
